import React from "react";
import { Link } from "react-router-dom";
import { Handshake } from "lucide-react";
import { COLORS } from "./dispute/theme";
import { useIsMobile, merge } from "../hooks/useIsMobile";
import { useHasPublicDisputes } from "../hooks/useHasPublicDisputes";

const MONO = "'DM Mono', monospace";
// The footer sets its own body font instead of inheriting the page's. Pages
// differ (HomeBeta/About resolve SANS to Inter, Communities and /login use DM
// Sans), and Inter's heavier strokes made the same #000 text read darker on the
// homepage than everywhere else. DM Sans is the lighter of the two and is
// loaded globally in public/index.html.
const BODY = "'DM Sans', -apple-system, BlinkMacSystemFont, 'Segoe UI', Inter, system-ui, sans-serif";
const NAV_SERIF = "'Playfair Display', 'Source Serif Pro', Georgia, 'Times New Roman', serif";

// The shared site footer (marketing + authenticated pages). Single source of
// truth — every SiteHeader page renders this so the footer,
// including the Privacy/Terms links, stays identical across the site.
// `textColor`/`bg`/`topBorder`/`accentColor` default to the sitewide look
// (white background, black text, blue top border) and can be overridden
// per-page if a page ever needs a different treatment. `accentColor` (falls
// back to `textColor`) colors just the logo, tagline, and column titles,
// leaving the links/copyright in `textColor`.
export default function SiteFooter({ textColor = "#000", bg = "#fff", topBorder = "rgba(26,94,196,0.2)", accentColor = "#1A5EC4" } = {}) {
  const isMobile = useIsMobile();
  // The public index only gets a link once it has at least one dispute in it.
  const hasPublicDisputes = useHasPublicDisputes();
  const accent = accentColor || textColor;
  const linkStyle = { ...s.footerColLink, color: textColor };
  const titleStyle = { ...s.footerColTitle, color: accent };
  const footerStyle = {
    ...s.footer,
    background: bg,
    ...(topBorder ? { borderTop: `1px solid ${topBorder}` } : null),
  };
  return (
    <footer style={footerStyle}>
      <div style={s.container}>
        <div style={merge(s.footerInner, s.footerInnerMobile, isMobile)}>
          <div style={s.footerBrand}>
            <Link to="/home" style={s.footerLogoLink}>
              <span style={{ ...s.brand, color: accent }}>
                <Handshake size={22} strokeWidth={1.75} style={{ color: accent }} />
                <span style={{ ...s.navLogo, color: accent }}>
                  disputef<span style={s.navLogoItalic}>AI</span>
                </span>
              </span>
            </Link>
            <p style={{ ...s.footerBrandText, color: textColor }}>
              Multi-party AI resolution for disputes, disagreements, and decisions.
            </p>
          </div>
          <div style={s.footerCol}>
            <h6 style={titleStyle}>Platform</h6>
            {/* <a href="/home#how" style={s.footerColLink}>How it works</a> */}
            {/* <a href="/home#panel" style={s.footerColLink}>The panel</a> */}
            {/* <a href="/home#pricing" style={s.footerColLink}>Pricing</a> */}
            {/* <a href="/home#faq" style={s.footerColLink}>FAQ</a> */}
            <Link to="/communities" style={linkStyle}>Community disputes</Link>
            {hasPublicDisputes && (
              <Link to="/disputes" style={linkStyle}>Public Disputes</Link>
            )}
          </div>
          {/* <div style={s.footerCol}>
            <h6 style={s.footerColTitle}>Disputes</h6>
            <span style={s.footerColLink}>Sports</span>
            <span style={s.footerColLink}>Pop culture</span>
            <span style={s.footerColLink}>Money</span>
            <span style={s.footerColLink}>Politics</span>
          </div> */}
          <div style={s.footerCol}>
            <h6 style={titleStyle}>Company</h6>
            <Link to="/about" style={linkStyle}>About</Link>
            <Link to="/privacy" style={linkStyle}>Privacy</Link>
            <Link to="/terms" style={linkStyle}>Terms</Link>
          </div>
        </div>
        <div style={{ ...merge(s.footerBottom, s.footerBottomMobile, isMobile), color: textColor }}>
          <span>© 2026 disputefai. All rights reserved.</span>
          <span>Decisions are advisory and do not constitute legal advice.</span>
        </div>
      </div>
    </footer>
  );
}

const s = {
  container: { maxWidth: 1200, margin: "0 auto", padding: "0 32px" },
  brand: { display: "inline-flex", alignItems: "center", gap: 8, color: COLORS.ink },
  navLogo: { fontFamily: NAV_SERIF, fontSize: 22, fontWeight: 600, letterSpacing: "-0.3px" },
  navLogoItalic: { fontStyle: "italic" },
  // `marginTop: auto` pins the footer to the bottom of any page whose wrapper
  // is a min-height flex column, so short pages don't leave it floating in the
  // middle. Harmless on non-flex wrappers. (`background` is set from the `bg`
  // prop in footerStyle above — don't set a competing one here.)
  // The footer fixes its own font metrics — family, size, line-height,
  // letter-spacing, alignment and smoothing — instead of inheriting them. Pages
  // disagree on every one of those (Communities 15px/1.55, pageStyles.root
  // 14px, HomeBeta nothing; three wrappers set -webkit-font-smoothing, which
  // changes stroke weight), and no element below sets a line-height, so the
  // link columns rendered visibly taller on some pages than others. `normal` is
  // what the homepage has always shown. Keep the chrome self-contained.
  footer: { color: "#fff", padding: "64px 0 32px", marginTop: "auto", fontFamily: BODY, fontSize: 14, lineHeight: "normal", letterSpacing: "normal", textAlign: "left", WebkitFontSmoothing: "antialiased" },
  // Three columns, three children: brand, Platform, Company. The old 4-column
  // track left a dead quarter on the right after the "Disputes" column was
  // commented out — keep this in step with the columns actually rendered.
  footerInner: { display: "grid", gridTemplateColumns: "2fr 1fr 1fr", gap: 48, paddingBottom: 48, borderBottom: "1px solid rgba(255,255,255,0.08)" },
  footerInnerMobile: { gridTemplateColumns: "1fr", gap: 32 },
  footerBrand: { fontSize: 14, lineHeight: 1.65 },
  footerLogoLink: { textDecoration: "none", color: "#fff", display: "inline-flex", marginBottom: 16 },
  footerBrandText: { color: "#fff", margin: 0, fontSize: 14, lineHeight: 1.65 },
  footerCol: { display: "flex", flexDirection: "column", gap: 11 },
  footerColTitle: { fontFamily: MONO, fontSize: 10.5, letterSpacing: "0.14em", textTransform: "uppercase", color: "#fff", marginBottom: 5, fontWeight: 500, marginTop: 0 },
  footerColLink: { color: "#fff", textDecoration: "none", fontSize: 14, transition: "color 0.15s", cursor: "pointer" },
  footerBottom: { paddingTop: 24, display: "flex", justifyContent: "space-between", flexWrap: "wrap", gap: 12, fontSize: 12.5, color: "#fff" },
  footerBottomMobile: { flexDirection: "column", gap: 8, textAlign: "center" },
};
